import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import SectionWrapper from '../SectionWrapper';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import { Flex, FormControl, FormLabel, Textarea, Text } from '@chakra-ui/react';
import SwitchField from '../SwitchField';
import { setFormField } from '../../../../store/redux/slices/form.slice';
import { FormState } from '../../../../store/types/form';
import FieldEditIndicator from '../FieldEditIndicator';
import { useEffect, useRef } from 'react';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';

const LegalConsent = () => {
  const formEnabled = useAppSelector((s) => s.form.enabled);
  const dataProcessingConsentText = useAppSelector(
    (s) => s.form.dataProcessingConsentText
  );
  const dataProcessingConsentRequired = useAppSelector(
    (s) => s.form.dataProcessingConsentRequired
  );
  const dispatch = useAppDispatch();
  const { handleFocus } = useTemplateFieldValidation('form');
  const errors = useAppSelector((s) => s.templateErrors);
  const dataProcessingConsentTextRef = useRef<HTMLDivElement>(null);
  const requireUserConsentRef = useRef<HTMLDivElement>(null);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      dataProcessingConsentText: dataProcessingConsentTextRef,
      dataProcessingConsentRequired: requireUserConsentRef,
    },
  });

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    dispatch(setFormField({ field, value }));
  };

  useEffect(() => {
    if (!dataProcessingConsentText) {
      dispatch(
        setFormField({ field: 'dataProcessingConsentRequired', value: false })
      );
    }
  }, [dataProcessingConsentText]);

  return (
    <SectionWrapper
      title="Legal consent"
      openOnLoad={true}
      id="legal-consent-section"
      subtitle="This text is shown to customers before a pass is issued. Use it to explain how their data will be used and to link to your privacy policy or terms."
    >
      <Flex direction="column" gap={4}>
        <Flex direction="column" gap={4}>
          <FieldEditIndicator
            fieldRef={dataProcessingConsentTextRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-40px"
            top="70px"
          >
            <FormControl isInvalid={!!errors['form.dataProcessingConsentText']}>
              <FormLabel variant="templateBuilderLabel">
                Consent message
              </FormLabel>
              <Textarea
                variant="outlined"
                value={dataProcessingConsentText}
                onChange={(e) =>
                  handleChange('dataProcessingConsentText', e.target.value)
                }
                placeholder="ex. By continuing, you agree to our Privacy Policy and Terms of Service."
                onFocus={() => handleFocus('dataProcessingConsentText')}
                isDisabled={!formEnabled}
              />
              <Text fontSize="12px" mt={4}>
                <em>You can include links by pasting full URLs.</em>
              </Text>
            </FormControl>
          </FieldEditIndicator>
          <FieldEditIndicator
            fieldRef={requireUserConsentRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-40px"
            top="50px"
          >
            <FormControl
              onClick={() => handleFocus('dataProcessingConsentRequired')}
            >
              <SwitchField
                value={dataProcessingConsentRequired}
                onChange={(value) => {
                  handleChange('dataProcessingConsentRequired', value);
                }}
                disabled={!formEnabled || !dataProcessingConsentText}
                label={<FormLabel variant="templateBuilderLabel" mb={0}>Require user consent to continue?</FormLabel>}
              />
            </FormControl>
          </FieldEditIndicator>
        </Flex>
      </Flex>
    </SectionWrapper>
  );
};

export default LegalConsent;
